/**
 * An affiliate's earnings (TMMPDM-0043, TMMAPI-0105). One table for the payload's one currency,
 * months in the order the server sent (newest first). Voided is its own column, struck when the
 * month has a voided amount, and is never added into a total — the server publishes none.
 */
import { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, ErrorState } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Amount, stickyTableContainerSx } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import type { AffiliateEarnings } from '@/api/tmm/types';
import { formatMonthYear } from '@/format';

interface AffiliateEarningsSectionProps {
  slug: string;
  affiliateId: number;
}

/** `YYYY-MM` as the settlement month label; the raw value if it isn't one. */
function earnedMonthLabel(value: string, locale: string): string {
  return /^\d{4}-\d{2}$/.test(value) ? formatMonthYear(`${value}-01`, locale) : value;
}

export function AffiliateEarningsSection({ slug, affiliateId }: AffiliateEarningsSectionProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [reloadToken, setReloadToken] = useState(0);
  const [earnings, setEarnings] = useState<AffiliateEarnings | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getAffiliateEarnings(slug, affiliateId)
      .then((body) => {
        if (cancelled) return;
        setEarnings(body);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setEarnings(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, affiliateId, reloadToken]);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('affiliates.earnings.loadError', 'Could not load earnings.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }
  if (earnings === null) return <LoadingSpinner />;
  if (earnings.months.length === 0) {
    return <EmptyState surface title={t('affiliates.earnings.empty', 'No earnings yet.')} />;
  }

  const currency = earnings.currency;
  const money = (minor: number) => <Amount minor={minor} currency={currency} locale={locale} />;
  const aria = t('affiliates.earnings.aria', 'Earnings by month in {{currency}}', { currency });

  return (
    <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
      <Table size="small" aria-label={aria}>
        <TableHead>
          <TableRow>
            <TableCell>{t('affiliates.earnings.col.month', 'Month')}</TableCell>
            <TableCell align="right">
              {t('affiliates.earnings.col.recurring', 'Recurring')}
            </TableCell>
            <TableCell align="right">{t('affiliates.earnings.col.bounty', 'Bounty')}</TableCell>
            <TableCell align="right">
              {t('affiliates.earnings.col.milestone', 'Milestone')}
            </TableCell>
            <TableCell align="right">{t('affiliates.earnings.col.pending', 'Pending')}</TableCell>
            <TableCell align="right">{t('affiliates.earnings.col.payable', 'Payable')}</TableCell>
            <TableCell align="right">{t('affiliates.earnings.col.voided', 'Voided')}</TableCell>
            <TableCell align="right">
              {t('affiliates.earnings.col.clawedBack', 'Clawed back')}
            </TableCell>
            <TableCell align="right">
              {t('affiliates.earnings.col.recovered', 'Recovered')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {earnings.months.map((row) => (
            <TableRow key={row.month}>
              <TableCell component="th" scope="row">
                {earnedMonthLabel(row.month, locale)}
              </TableCell>
              <TableCell align="right">{money(row.recurringMinor)}</TableCell>
              <TableCell align="right">{money(row.bountyMinor)}</TableCell>
              <TableCell align="right">{money(row.milestoneMinor)}</TableCell>
              <TableCell align="right">{money(row.pendingMinor)}</TableCell>
              <TableCell align="right">{money(row.payableMinor)}</TableCell>
              <TableCell
                align="right"
                sx={row.voidedMinor !== 0 ? { textDecoration: 'line-through' } : undefined}
              >
                {money(row.voidedMinor)}
              </TableCell>
              <TableCell align="right">{money(row.clawedBackMinor)}</TableCell>
              <TableCell align="right">{money(row.recoveredMinor)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
        {currency}
      </Typography>
    </TableContainer>
  );
}
